import React, { FC, useState } from 'react';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import PaymentReport from 'components/Payment/PaymentReport/PaymentReport';
import PrintWindow from 'components/Portal/PrintWindow/PrintWindow';
import { exportJSONToCSV, downloadCSV } from 'helpers';
import { AppGroup } from 'reduxState/store/application/types';
import { useLazyGetPaymentDetailQuery } from 'reduxState/store/payment/api';
import { isPaymentDetail } from 'reduxState/store/payment/typeGuards';
import { PaymentDetailCommission, IPayment } from 'reduxState/store/payment/types';
import './PaymentReportDownload.scss';

interface IPaymentReportDownloadProps {
  appGroup: AppGroup;
  payment: IPayment;
}

const PaymentReportDownload: FC<React.PropsWithChildren<IPaymentReportDownloadProps>> = ({ appGroup, payment }) => {
  const displayDate = format(new Date(payment.CreatedDate), 'MMM dd, yyyy');

  const [showReport, setShowReport] = useState(false);
  const [microsoftLoading, setMicrosoftLoading] = useState(false);
  const [csvError, setCSVError] = useState(false);
  const [getPaymentDetail] = useLazyGetPaymentDetailQuery();

  const toggleShowReport = (): void => setShowReport(prev => !prev);

  const closePrintWindow = (): void => setShowReport(false);

  const downloadCSVClick = async (
    appName: string,
    applicationId: string,
    paymentId: number,
    isMicrosoftLoading: boolean,
  ) => {
    // Don't download the report again if it's in the middle of loading
    if (isMicrosoftLoading === true) {
      return;
    }

    setMicrosoftLoading(true);
    try {
      const { data, error } = await getPaymentDetail({ appId: applicationId, paymentId });
      if (error || !data || !isPaymentDetail(data)) {
        setCSVError(true);
        toast.error('There was an issue downloading the payment report CSV');
        return;
      }

      const columns = Object.keys(data.Commissions[0]) as Array<keyof PaymentDetailCommission>;
      const csv = exportJSONToCSV(
        data.Commissions,
        columns.map(key => ({
          accessor: key,
          // add spaces between words for correct CSV header
          name: key.replace(/([a-z])([A-Z])/g, '$1 $2'),
        })),
      );
      downloadCSV(csv, generateReportName(appName, payment.CreatedDate, payment.Processor) + '.csv', window.document);
      setMicrosoftLoading(false);
    } catch (error) {
      console.error(error);
      setCSVError(true);
    }
  };

  const isMicrosoft = [53, 57, 99, 183, 201, 210].includes(appGroup.adminId); // TODO: verify that all ids should be included

  const generateReportName = (name: string, createdDate: string, processor: string) => {
    const downloadDate = format(new Date(createdDate), 'MM-dd-yyyy');

    let parsedProcessor: string;
    if (processor === 'CHECK') {
      parsedProcessor = 'Partner';
    } else {
      parsedProcessor = 'User';
    }
    return `Wildfire ${appGroup.adminName} Payment Report ${downloadDate} ${parsedProcessor}`;
  };

  return (
    <div className="payment-report-download">
      {appGroup ? (
        <>
          {isMicrosoft && !csvError && (
            <div
              className="link"
              onClick={() =>
                downloadCSVClick(appGroup.adminName, appGroup.adminId.toString(), payment.PaymentID, microsoftLoading)
              }
            >
              {microsoftLoading
                ? 'Downloading CSV. One moment please'
                : generateReportName(appGroup.adminName, payment.CreatedDate, payment.Processor)}
            </div>
          )}
          {isMicrosoft && csvError && <div className="link">Error Retrieving report</div>}
          {!isMicrosoft && (
            <div className="link" onClick={toggleShowReport}>
              {showReport ? 'Close' : generateReportName(appGroup.adminName, payment.CreatedDate, payment.Processor)}
            </div>
          )}
          {showReport && (
            <PrintWindow
              onClose={closePrintWindow}
              windowTitle={generateReportName(appGroup.adminName, payment.CreatedDate, payment.Processor)}
            >
              <PaymentReport appGroup={appGroup} paymentId={payment.PaymentID} displayDate={displayDate} />
            </PrintWindow>
          )}
        </>
      ) : null}
    </div>
  );
};

export default PaymentReportDownload;
